import { App } from "antd";
import { useTranslation } from "react-i18next";
import {
  useConfiguration,
  useUpdateNonFiscalDisplay,
  useUpdateOrderingCatalogFilter,
  useUpdateOrderingColorBy,
  useUpdateOrderingGuestCount,
  useUpdateOrderingQuickClose,
  useUpdateOrderingSearchField,
  useUpdateOrderingSearchScope,
  useUpdateOrderingSharedNumbering,
  useUpdateOrderingSuccessNotifications,
  useUpdateOrderingTableOrder,
} from "../../../../hooks/useConfiguration";
import type {
  CatalogFilter,
  ColorBy,
  NonFiscalDisplayMode,
  OrderingGuestCount,
  OrderingSearchScope,
  OrderingTableOrder,
} from "../../../../api/configuration";
import { useAppSelector } from "../../../../store/hooks";
import { SettingsSection } from "./SettingsSection";
import { SettingRow, type SettingOption } from "./SettingRow";
import { CatalogFilterIllustration } from "./CatalogFilterIllustration";
import { GuestCountFlowIllustration } from "./GuestCountFlowIllustration";

/**
 * Настройките НА РЕСТОРАНТА (таблица `configuration` в базата на ресторанта,
 * общи за всички терминали) на страницата „Маркиране" — четири секции: маси,
 * поръчки, известия, каталог. Личните настройки на сервитьора НЕ са тук
 * нарочно — сменят се от бутоните в самото „Маркиране" (и темата от
 * профилното меню), защото сервитьорът може да няма права до Конфигурация.
 * Всеки ред има свой PUT; отговорът се слива в store-а от hook-а.
 */
export function RestaurantSettingsSection() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { configuration } = useConfiguration();
  const { mutate: updateNonFiscal, isPending: isSavingNonFiscal } =
    useUpdateNonFiscalDisplay();
  const { mutate: updateQuickClose, isPending: isSavingQuickClose } =
    useUpdateOrderingQuickClose();
  const { mutate: updateSharedNumbering, isPending: isSavingSharedNumbering } =
    useUpdateOrderingSharedNumbering();
  const { mutate: updateTableOrder, isPending: isSavingTableOrder } =
    useUpdateOrderingTableOrder();
  const {
    mutate: updateSuccessNotifications,
    isPending: isSavingSuccessNotifications,
  } = useUpdateOrderingSuccessNotifications();
  const { mutate: updateCatalogFilter, isPending: isSavingCatalogFilter } =
    useUpdateOrderingCatalogFilter();
  const { mutate: updateColorBy, isPending: isSavingColorBy } =
    useUpdateOrderingColorBy();
  const { mutate: updateSearchField, isPending: isSavingSearchField } =
    useUpdateOrderingSearchField();
  const { mutate: updateSearchScope, isPending: isSavingSearchScope } =
    useUpdateOrderingSearchScope();
  const { mutate: updateGuestCount, isPending: isSavingGuestCount } =
    useUpdateOrderingGuestCount();
  // Продаваемото НЕ се казва „продукт": „асортимент", а в режим „със склад"
  // (organizations.uses_warehouse, идва с /user/info) — „рецепта". Същото
  // разклонение като useOrderedItemKindKeys в справките.
  const usesWarehouse = useAppSelector(
    (s) => s.auth.user?.uses_warehouse === true,
  );
  const sellableSuffix = usesWarehouse ? "Recipes" : "Assortments";

  const toasts = {
    onSuccess: () => message.success(t("orderingSettings.saveSuccess")),
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      message.error(detail || t("orderingSettings.saveError"));
    },
  };

  const sharedNumberingOptions: SettingOption<boolean>[] = [
    {
      value: false,
      label: t("orderingSettings.optionSharedNumberingOff"),
      hint: t("orderingSettings.hintSharedNumberingOff"),
    },
    {
      value: true,
      label: t("orderingSettings.optionSharedNumberingOn"),
      hint: t("orderingSettings.hintSharedNumberingOn"),
    },
  ];

  const tableOrderOptions: SettingOption<OrderingTableOrder>[] = [
    {
      value: "chronological",
      label: t("orderingSettings.optionTableOrderChronological"),
      hint: t("orderingSettings.hintTableOrderChronological"),
    },
    {
      value: "alphanumeric",
      label: t("orderingSettings.optionTableOrderAlphanumeric"),
      hint: t("orderingSettings.hintTableOrderAlphanumeric"),
    },
  ];

  const nonFiscalOptions: SettingOption<NonFiscalDisplayMode>[] = [
    {
      value: "never",
      label: t("orderingSettings.optionNonFiscalNever"),
      hint: t("orderingSettings.hintNonFiscalNever"),
    },
    {
      value: "always",
      label: t("orderingSettings.optionNonFiscalAlways"),
      hint: t("orderingSettings.hintNonFiscalAlways"),
    },
    {
      value: "on_demand",
      label: t("orderingSettings.optionNonFiscalOnDemand"),
      hint: t("orderingSettings.hintNonFiscalOnDemand"),
    },
  ];

  const guestCountOptions: SettingOption<OrderingGuestCount>[] = [
    {
      value: "off",
      label: t("orderingSettings.optionGuestCountOff"),
      hint: t("orderingSettings.hintGuestCountOff"),
    },
    {
      value: "new_table",
      label: t("orderingSettings.optionGuestCountNewTable"),
      hint: t("orderingSettings.hintGuestCountNewTable"),
    },
    {
      value: "close",
      label: t("orderingSettings.optionGuestCountClose"),
      hint: t("orderingSettings.hintGuestCountClose"),
    },
  ];

  const quickCloseOptions: SettingOption<boolean>[] = [
    {
      value: false,
      label: t("orderingSettings.optionQuickCloseOff"),
      hint: t("orderingSettings.hintQuickCloseOff"),
    },
    {
      value: true,
      label: t("orderingSettings.optionQuickCloseOn"),
      hint: t("orderingSettings.hintQuickCloseOn"),
    },
  ];

  const successNotificationsOptions: SettingOption<boolean>[] = [
    {
      value: false,
      label: t("orderingSettings.optionSuccessNotificationsOff"),
      hint: t("orderingSettings.hintSuccessNotificationsOff"),
    },
    {
      value: true,
      label: t("orderingSettings.optionSuccessNotificationsOn"),
      hint: t("orderingSettings.hintSuccessNotificationsOn"),
    },
  ];

  const catalogFilterOptions: SettingOption<CatalogFilter>[] = [
    {
      value: "groups",
      label: t("orderingSettings.optionFilterGroups"),
      hint: t("orderingSettings.hintFilterGroups"),
    },
    {
      value: "stations",
      label: t("orderingSettings.optionFilterStations"),
      hint: t("orderingSettings.hintFilterStations"),
    },
  ];

  const searchFieldOptions: SettingOption<boolean>[] = [
    {
      value: false,
      label: t("orderingSettings.optionSearchFieldOff"),
      hint: t("orderingSettings.hintSearchFieldOff"),
    },
    {
      value: true,
      label: t("orderingSettings.optionSearchFieldOn"),
      hint: t("orderingSettings.hintSearchFieldOn"),
    },
  ];

  const searchScopeOptions: SettingOption<OrderingSearchScope>[] = [
    {
      value: "menu",
      label: t("orderingSettings.optionSearchScopeMenu"),
      hint: t("orderingSettings.hintSearchScopeMenu"),
    },
    {
      value: "group",
      label: t("orderingSettings.optionSearchScopeGroup"),
      hint: t("orderingSettings.hintSearchScopeGroup"),
    },
  ];

  const colorByOptions: SettingOption<ColorBy>[] = [
    {
      value: "group",
      label: t("orderingSettings.optionColorGroup"),
      hint: t(`orderingSettings.hintColorGroup${sellableSuffix}`),
    },
    {
      value: "station",
      label: t("orderingSettings.optionColorStation"),
      hint: t(`orderingSettings.hintColorStation${sellableSuffix}`),
    },
    {
      value: "none",
      label: t("orderingSettings.optionColorNone"),
      hint: t("orderingSettings.hintColorNone"),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <SettingsSection
        kicker={t("orderingSettings.sectionTablesKicker")}
        title={t("orderingSettings.sectionTablesTitle")}
        count={4}
      >
        <SettingRow
          name={t("orderingSettings.sharedNumberingName")}
          description={t("orderingSettings.sharedNumberingDesc")}
          options={sharedNumberingOptions}
          value={configuration.orderingSharedNumbering}
          saving={isSavingSharedNumbering}
          onChange={(enabled) => updateSharedNumbering(enabled, toasts)}
        />
        <SettingRow
          name={t("orderingSettings.tableOrderName")}
          description={t("orderingSettings.tableOrderDesc")}
          options={tableOrderOptions}
          value={configuration.orderingTableOrder}
          saving={isSavingTableOrder}
          onChange={(order) => updateTableOrder(order, toasts)}
        />
        <SettingRow
          name={t("orderingSettings.nonFiscalName")}
          description={t("orderingSettings.nonFiscalDesc")}
          options={nonFiscalOptions}
          value={configuration.nonFiscalDisplayMode}
          saving={isSavingNonFiscal}
          onChange={(mode) => updateNonFiscal(mode, toasts)}
        />
        <SettingRow
          name={t("orderingSettings.guestCountName")}
          description={t("orderingSettings.guestCountDesc")}
          options={guestCountOptions}
          value={configuration.orderingGuestCount}
          saving={isSavingGuestCount}
          onChange={(mode) => updateGuestCount(mode, toasts)}
          illustration={
            <GuestCountFlowIllustration
              value={configuration.orderingGuestCount}
            />
          }
        />
      </SettingsSection>

      <SettingsSection
        kicker={t("orderingSettings.sectionOrdersKicker")}
        title={t("orderingSettings.sectionOrdersTitle")}
        count={1}
      >
        <SettingRow
          name={t("orderingSettings.quickCloseName")}
          description={t("orderingSettings.quickCloseDesc")}
          options={quickCloseOptions}
          value={configuration.orderingQuickClose}
          saving={isSavingQuickClose}
          onChange={(enabled) => updateQuickClose(enabled, toasts)}
        />
      </SettingsSection>

      <SettingsSection
        kicker={t("orderingSettings.sectionNotificationsKicker")}
        title={t("orderingSettings.sectionNotificationsTitle")}
        count={1}
      >
        <SettingRow
          name={t("orderingSettings.successNotificationsName")}
          description={t("orderingSettings.successNotificationsDesc")}
          options={successNotificationsOptions}
          value={configuration.orderingSuccessNotifications}
          saving={isSavingSuccessNotifications}
          onChange={(enabled) => updateSuccessNotifications(enabled, toasts)}
        />
      </SettingsSection>

      <SettingsSection
        kicker={t("orderingSettings.sectionCatalogKicker")}
        title={t("orderingSettings.sectionCatalogTitle")}
        count={4}
      >
        <SettingRow
          name={t("orderingSettings.catalogFilterName")}
          description={t("orderingSettings.catalogFilterDesc")}
          options={catalogFilterOptions}
          value={configuration.catalogFilter}
          saving={isSavingCatalogFilter}
          onChange={(catalogFilter) =>
            updateCatalogFilter(catalogFilter, toasts)
          }
          illustration={
            <CatalogFilterIllustration value={configuration.catalogFilter} />
          }
        />
        <SettingRow
          name={t(`orderingSettings.colorByName${sellableSuffix}`)}
          description={t("orderingSettings.colorByDesc")}
          options={colorByOptions}
          value={configuration.colorBy}
          saving={isSavingColorBy}
          onChange={(colorBy) => updateColorBy(colorBy, toasts)}
        />
        <SettingRow
          name={t("orderingSettings.searchFieldName")}
          description={t("orderingSettings.searchFieldDesc")}
          options={searchFieldOptions}
          value={configuration.orderingSearchField}
          saving={isSavingSearchField}
          onChange={(enabled) => updateSearchField(enabled, toasts)}
        />
        <SettingRow
          name={t("orderingSettings.searchScopeName")}
          description={t("orderingSettings.searchScopeDesc")}
          options={searchScopeOptions}
          value={configuration.orderingSearchScope}
          saving={isSavingSearchScope}
          onChange={(scope) => updateSearchScope(scope, toasts)}
        />
      </SettingsSection>
    </div>
  );
}
